import logoCannect from '@cannect/assets/svg/logo2.svg'
import { getBrandHeaderLogo } from '@cannect/new-components/organisms/HeaderAssociation/brandLogo'
import { useBrand } from 'hooks/useBrand'
import { TRecipePrescription, TRecipeRealPatient } from '@cannect/services/resources/recipeAccess'
import { formatRecipeDateTime } from '../formatRecipeDate'

type ReceiptCardProps = {
  patient: TRecipeRealPatient
  emission: string
  token: string
  prescription?: TRecipePrescription
  className?: string
}

// Card "Receituário" do D3/B4 (`Desktop-B-receita-aberta.dc.html`,
// `B-Receita-aberta.dc.html`). Prescrição/prescritor (decisão 45, contrato
// publicado): item alopático vem sem fabricante (`manufacturer: null`);
// prescritor pode vir `null` — nesse caso a linha some, sem quebrar.
// NUNCA escrever "assinatura digital ICP-Brasil" (decisão 45 — não é
// verificável após a emissão).
export const ReceiptCard = ({ patient, emission, token, prescription, className }: ReceiptCardProps) => {
  const { config } = useBrand()
  const hasItems = !!prescription?.items?.length
  const prescriber = prescription?.prescriber
  // Achado 52 (humano, captura real): `config.logo.main` ('/logo-cannect.svg')
  // não resolve pra arquivo nenhum — não existe em `public/` e nenhum outro
  // componente do repo o usa. O header real importa o SVG pelo pipeline de
  // assets do Vite (`HeaderDesktop.tsx:1`, `logo2.svg`) e o whitelabel usa
  // `getBrandHeaderLogo` (`HeaderAssociation/brandLogo.ts`) — reuso os dois
  // em vez de inventar um terceiro caminho.
  const logoSrc = config.key === 'cannect' ? logoCannect : getBrandHeaderLogo(config.key)

  return (
    <div className={`flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-5 lg:p-7 ${className || ''}`}>
      <div className="flex items-center justify-between">
        <img src={logoSrc} alt={config.name} className="h-4 lg:h-[18px]" />
        <span className="text-xs text-gray-500">Receituário · {formatRecipeDateTime(emission)}</span>
      </div>
      <div className="h-px bg-gray-100" />
      <div className="flex gap-8">
        <div className="flex-grow">
          <p className="text-xs text-gray-500">Paciente</p>
          <p className="text-sm font-semibold text-gray-950 lg:text-[15px]">{patient.name}</p>
        </div>
        {/* Decisão 48 (Esquadro): no mobile o código já aparece fora do card (ou já está na
            URL); o protótipo mobile não repete o campo aqui, só o desktop. */}
        <div className="hidden flex-grow sm:block">
          <p className="text-xs text-gray-500">Código</p>
          <p className="text-sm font-semibold text-gray-950 lg:text-[15px]">{token}</p>
        </div>
      </div>

      {hasItems && (
        <>
          <div className="h-px bg-gray-100" />
          <div className="flex flex-col gap-3">
            <p className="text-xs text-gray-500">Prescrição</p>
            {prescription.items.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`${item.name}-${index}`}>
                <p className="text-sm font-semibold leading-snug text-gray-950 lg:text-[15px]">
                  {item.manufacturer ? `${item.manufacturer} · ${item.name}` : item.name}
                </p>
                <p className="mt-0.5 text-xs text-gray-500">
                  {item.quantity} frasco(s) · {item.posology}
                </p>
              </div>
            ))}
          </div>
        </>
      )}

      {prescriber && (
        <>
          <div className="h-px bg-gray-100" />
          <div>
            <p className="text-sm font-semibold text-gray-950">{prescriber.name}</p>
            <p className="text-xs text-gray-500">
              {prescriber.council} {prescriber.council_number} - {prescriber.council_uf}
            </p>
          </div>
        </>
      )}
    </div>
  )
}
